import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './Pagination.css';

/** [1, '...', 4, 5, 6, '...', 12]: always the first and last page and the pages around the current one. */
export const getPageItems = (page, totalPages) => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

    const items = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) items.push('...');
    for (let current = start; current <= end; current += 1) items.push(current);
    if (end < totalPages - 1) items.push('...');
    items.push(totalPages);
    return items;
};

/**
 * Page buttons with a "Showing 1-10 of 42 users" summary. Renders nothing when there is one page.
 *
 * @param {{ page: number, totalPages: number, totalItems: number, pageSize: number,
 *           onPageChange: (page: number) => void, disabled?: boolean, itemLabel?: string }} props
 */
const Pagination = ({ page, totalPages, totalItems, pageSize, onPageChange, disabled = false, itemLabel = 'items' }) => {
    if (!totalPages || totalPages <= 1) return null;

    const first = (page - 1) * pageSize + 1;
    const last = Math.min(page * pageSize, totalItems);
    const goTo = (target) => {
        if (disabled || target < 1 || target > totalPages || target === page) return;
        onPageChange(target);
    };

    return (
        <nav className="app-pager" aria-label="Pagination">
            <span className="app-pager-info">
                Showing {first}-{last} of {totalItems} {itemLabel}
            </span>

            <div className="app-pager-buttons">
                <button
                    type="button"
                    className="app-pager-btn app-pager-nav"
                    onClick={() => goTo(page - 1)}
                    disabled={disabled || page <= 1}
                    aria-label="Previous page"
                >
                    <ChevronLeft size={16} aria-hidden="true" />
                </button>

                {getPageItems(page, totalPages).map((item, index) =>
                    item === '...' ? (
                        <span key={`gap-${index}`} className="app-pager-gap" aria-hidden="true">...</span>
                    ) : (
                        <button
                            key={item}
                            type="button"
                            className={`app-pager-btn${item === page ? ' active' : ''}`}
                            onClick={() => goTo(item)}
                            disabled={disabled}
                            aria-label={`Page ${item}`}
                            aria-current={item === page ? 'page' : undefined}
                        >
                            {item}
                        </button>
                    )
                )}

                <button
                    type="button"
                    className="app-pager-btn app-pager-nav"
                    onClick={() => goTo(page + 1)}
                    disabled={disabled || page >= totalPages}
                    aria-label="Next page"
                >
                    <ChevronRight size={16} aria-hidden="true" />
                </button>
            </div>
        </nav>
    );
};

export default Pagination;
